import { Avatar, Button, Icon, Modal } from "antd";
import React, { memo, useEffect, useMemo, useState } from "react";
import { execQuery } from "../../../../../components/http.request";
import { xmlProps } from "../../../constants";
import styles from "./imageDisplay.module.css";

interface KeyObject {
    [key: string]: string | number | bigint | KeyObject | undefined;
}

interface ImageResponse {
    erro: string | undefined;
    result: { foto: string }[] | undefined;
}

interface ImageDisplayProps {
    itemData: KeyObject;
    showAvatar?: boolean;
}

const IMAGE_TYPE = {
    user: "user",
    default: "file-image",
};

export const ImageDisplay = memo(
    ({ itemData, showAvatar = false }: ImageDisplayProps) => {
        const [imageContent, setImageContent] = useState<string | null>(null);
        const [preview, setPreview] = useState<boolean>(false);
        const [loadingImage, setLoadingImage] = useState<boolean>(true);

        const currentValue = itemData?.value;
        const dataSource = itemData[xmlProps.imagem] as string;
        const imageType = itemData["@tipo_imagem"];

        useEffect(() => {
            if (!currentValue || !dataSource) {
                setLoadingImage(false);
                return;
            }

            if (Number(currentValue) < 0) {
                setLoadingImage(false);
                return;
            }

            setLoadingImage(true);

            if (currentValue && dataSource) {
                execQuery({ query: dataSource, id: currentValue })
                    .then((response: ImageResponse) => {
                        const { erro, result } = response;

                        if (erro || !result || result.length === 0) {
                            setImageContent(null);
                            return;
                        }

                        const imageToDisplay = result[0].foto;

                        setImageContent(imageToDisplay);
                    })
                    .finally(() => {
                        setLoadingImage(false);
                    });
            }
        }, [currentValue, dataSource]);

        function handlePreviewImage() {
            setPreview(true);
        }

        function handleCancelPreviewImage() {
            setPreview(false);
        }

        const defaultIcon = useMemo(() => {
            if (imageType === IMAGE_TYPE.user) {
                return IMAGE_TYPE.user;
            }

            return IMAGE_TYPE.default;
        }, [imageType]);

        return (
            <>
                {showAvatar ? (
                    imageContent ? (
                        <Button type="link" style={{ padding: 0 }} onClick={handlePreviewImage}>
                            <Avatar src={imageContent} style={{ marginRight: 6 }} />
                        </Button>
                    ) : (
                        <Avatar style={{ marginRight: 6 }} icon={loadingImage ? "loading" : "user"} />
                    )
                ) : (
                    <div className={styles.divRoot}>
                        <div
                            className={styles["div-image"]}
                            style={{
                                cursor: imageContent ? "pointer" : "default",
                            }}
                        >
                            {loadingImage ? (
                                <Icon type={"loading"} style={{ fontSize: 32 }} />
                            ) : imageContent ? (
                                <img
                                    src={imageContent}
                                    alt=""
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        cursor: imageContent ? "pointer" : "initial",
                                        objectFit: "contain",
                                    }}
                                />
                            ) : (
                                <Icon type={defaultIcon} style={{ fontSize: 32 }} />
                            )}
                        </div>
                        {imageContent && (
                            <div className={styles["image-root-hover"]}>
                                <div className={styles["div-image-hover"]} onClick={handlePreviewImage}>
                                    <Icon type="eye" style={{ color: "#fff" }} />
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {preview && imageContent && (
                    <Modal visible footer={null} onCancel={handleCancelPreviewImage}>
                        <img alt="" style={{ width: "100%" }} src={imageContent!} />
                    </Modal>
                )}
            </>
        );
    },
    (prevProps: any, nextProps: any) => {
        return prevProps.itemData.value === nextProps.itemData.value;
    }
);
